import { useCallback, useEffect, useRef, useState } from 'react';

interface PickupLocation {
  id: string;
  name: string;
  address: string;
  landmark?: string;
  area?: string;
  city: string;
  state: string;
  zipCode?: string;
  pincode?: string;
  phone: string;
  email?: string;
  timings?: string;
  mapUrl?: string;
  isActive: boolean;
  distance?: number;
  transportProviderName?: string;
}

interface WorkerResult<T> {
  data: T;
  error?: string;
}

export const useShippingWorker = () => {
  const workerRef = useRef<Worker | null>(null);
  const [isWorkerSupported, setIsWorkerSupported] = useState(true);
  const pendingCallbacks = useRef<Map<string, (result: any) => void>>(new Map());
  const messageIdCounter = useRef(0);

  // Initialize worker on mount
  useEffect(() => {
    if (typeof Worker !== 'undefined') {
      try {
        // Create worker with Vite's special import syntax
        workerRef.current = new Worker(
          new URL('../workers/shipping.worker.ts', import.meta.url),
          { type: 'module' }
        );

        // Set up message handler
        workerRef.current.onmessage = (event) => {
          const { type, error, messageId } = event.data;
          
          // Handle responses with messageId for request-response pattern
          if (messageId && pendingCallbacks.current.has(messageId)) {
            const callback = pendingCallbacks.current.get(messageId);
            pendingCallbacks.current.delete(messageId);
            callback?.(event.data);
            return;
          }

          // Handle broadcast messages
          if (type === 'ERROR') {
            console.error('Worker error:', error);
          }
        };

        workerRef.current.onerror = (error) => {
          console.error('Worker error:', error);
          setIsWorkerSupported(false);
        };
      } catch (error) {
        console.warn('Web Worker not supported:', error);
        setIsWorkerSupported(false);
      }
    } else {
      setIsWorkerSupported(false);
    }

    // Cleanup
    return () => {
      workerRef.current?.terminate();
      pendingCallbacks.current.clear();
    };
  }, []);

  // Send message to worker with promise-based response
  const sendToWorker = useCallback(<T>(type: string, data: any): Promise<WorkerResult<T>> => {
    return new Promise((resolve) => {
      const messageId = `${type}_${++messageIdCounter.current}`;
      
      if (workerRef.current && isWorkerSupported) {
        // Use worker
        pendingCallbacks.current.set(messageId, (result) => {
          resolve({ data: result.data, error: result.error });
        });
        
        workerRef.current.postMessage({ type, data, messageId });
      } else {
        // Fallback to main thread
        setTimeout(() => {
          try {
            const result = performFallbackCalculation(type, data);
            resolve({ data: result as T });
          } catch (error) {
            resolve({ 
              data: null as T, 
              error: error instanceof Error ? error.message : 'Unknown error' 
            });
          }
        }, 0);
      }
    });
  }, [isWorkerSupported]);

  // Search locations
  const searchLocations = useCallback(async (
    locations: PickupLocation[],
    searchTerm: string
  ) => {
    const result = await sendToWorker<PickupLocation[]>('SEARCH_LOCATIONS', {
      locations,
      searchTerm
    });
    
    return result.data || [];
  }, [sendToWorker]);

  // Filter locations
  const filterLocations = useCallback(async (
    locations: PickupLocation[],
    filters: {
      city?: string;
      state?: string;
      isActive?: boolean;
    }
  ) => {
    const result = await sendToWorker<PickupLocation[]>('FILTER_LOCATIONS', {
      locations,
      ...filters
    });
    
    return result.data || [];
  }, [sendToWorker]);

  // Sort locations
  const sortLocations = useCallback(async (
    locations: PickupLocation[],
    sortBy: 'name' | 'city' | 'state',
    order: 'asc' | 'desc' = 'asc'
  ) => {
    const result = await sendToWorker<PickupLocation[]>('SORT_LOCATIONS', {
      locations,
      sortBy,
      order
    });
    
    return result.data || [];
  }, [sendToWorker]);

  return {
    isWorkerSupported,
    searchLocations,
    filterLocations,
    sortLocations
  };
};

// Fallback calculations for non-worker environments
function performFallbackCalculation(type: string, data: any): any {
  switch (type) {
    case 'SEARCH_LOCATIONS': {
      const { locations, searchTerm } = data;
      
      if (!searchTerm.trim()) return locations;
      
      const searchLower = searchTerm.toLowerCase();
      
      return locations.filter((location: PickupLocation) => {
        return (
          (location.name && location.name.toLowerCase().includes(searchLower)) ||
          (location.address && location.address.toLowerCase().includes(searchLower)) ||
          (location.landmark && location.landmark.toLowerCase().includes(searchLower)) ||
          (location.area && location.area.toLowerCase().includes(searchLower)) ||
          (location.city && location.city.toLowerCase().includes(searchLower)) ||
          (location.state && location.state.toLowerCase().includes(searchLower)) ||
          (location.zipCode && location.zipCode.includes(searchTerm)) ||
          (location.pincode && location.pincode.includes(searchTerm)) ||
          (location.phone && location.phone.includes(searchTerm)) ||
          (location.email && location.email.toLowerCase().includes(searchLower)) ||
          (location.timings && location.timings.toLowerCase().includes(searchLower)) ||
          (location.transportProviderName && location.transportProviderName.toLowerCase().includes(searchLower))
        );
      });
    }
    
    case 'FILTER_LOCATIONS': {
      const { locations, city, state, isActive } = data;
      
      return locations.filter((location: PickupLocation) => {
        const matchesCity = !city || location.city.toLowerCase() === city.toLowerCase();
        const matchesState = !state || location.state.toLowerCase() === state.toLowerCase();
        const matchesActive = isActive === undefined || location.isActive === isActive;
        
        return matchesCity && matchesState && matchesActive;
      });
    }
    
    case 'SORT_LOCATIONS': {
      const { locations, sortBy, order } = data;
      
      return [...locations].sort((a: PickupLocation, b: PickupLocation) => {
        let comparison = 0;
        
        switch (sortBy) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'city':
            comparison = a.city.localeCompare(b.city);
            break;
          case 'state':
            comparison = a.state.localeCompare(b.state);
            break;
        }
        
        return order === 'asc' ? comparison : -comparison;
      });
    }
    
    default:
      throw new Error(`Unknown calculation type: ${type}`);
  }
}